import React, { useState } from 'react';
import {
  Settings, Bell, Shield, CreditCard, Users, Globe, Moon, Sun,
  Check, X, Edit, Save, Trash2, Plus, Eye, EyeOff, Lock,
  Mail, Phone, MapPin, Calendar, AlertTriangle, CheckCircle,
  Download, Upload, Smartphone, Monitor, Laptop, Key, Leaf,
  Heart, Award, Target, Zap, Filter, Search
} from 'lucide-react';

const SettingsComponent = ({ company }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isEditing, setIsEditing] = useState({});

  const [settings, setSettings] = useState({
    general: {
      companyName: company.name,
      email: company.email,
      phone: "+33 1 23 45 67 89",
      website: "https://greentech-solutions.com",
      timezone: "Europe/Paris",
      language: "fr",
      currency: "EUR"
    },
    notifications: {
      emailNotifications: true,
      smsNotifications: false,
      pushNotifications: true,
      newApplications: true,
      projectUpdates: true,
      messageReceived: true,
      weeklyReports: true,
      marketingEmails: false,
      securityAlerts: true
    },
    privacy: {
      profileVisibility: "public",
      searchEngineIndexing: true,
      dataSharing: false,
      analytics: true,
      thirdPartyIntegrations: true,
      sessionTimeout: 30
    },
    billing: {
      plan: "Premium",
      nextBilling: "2024-02-15",
      paymentMethod: "**** **** **** 1234",
      autoRenew: true,
      invoiceEmail: company.email,
      billingAddress: {
        street: "123 Rue de la Paix",
        city: "Paris",
        postal: "75001",
        country: "France"
      }
    },
    team: {
      maxUsers: 10,
      currentUsers: 3,
      defaultRole: "member",
      requireApproval: true,
      ssoEnabled: false
    },
    api: {
      enabled: true,
      rateLimit: 1000,
      webhooksEnabled: true,
      apiKeys: [
        { id: 1, name: "Production API", key: "pk_live_***", created: "2024-01-10", lastUsed: "2024-01-15" },
        { id: 2, name: "Development API", key: "pk_test_***", created: "2024-01-05", lastUsed: "2024-01-14" }
      ]
    }
  });

  const tabs = [
    { id: 'general', label: 'Général', icon: Settings },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'privacy', label: 'Confidentialité', icon: Shield },
    { id: 'billing', label: 'Facturation', icon: CreditCard },
    { id: 'team', label: 'Équipe', icon: Users },
    { id: 'api', label: 'API & Intégrations', icon: Globe }
  ];

  const handleToggle = (section, key) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: !prev[section][key]
      }
    }));
  };

  const handleInputChange = (section, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  const toggleEdit = (section) => {
    setIsEditing(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const ToggleSwitch = ({ enabled, onChange, label, description }) => (
    <div className="flex items-center justify-between py-3">
      <div className="flex-1">
        <h4 className="text-sm font-medium text-gray-900">{label}</h4>
        {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
      </div>
      <button
        onClick={onChange}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
          enabled ? 'bg-green-500' : 'bg-gray-200'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
          <Settings className="w-8 h-8 mr-3 text-green-500" />
          Paramètres
        </h1>
        <p className="text-gray-600">
          Gérez vos préférences, la sécurité et les paramètres de votre compte entreprise.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar */}
        <div className="lg:w-64 bg-white rounded-2xl border border-gray-100 p-6">
          <nav className="space-y-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </nav>

          {/* Quick Actions */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Actions rapides</h3>
            <div className="space-y-2">
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors duration-200">
                <Download className="w-4 h-4" />
                <span>Exporter données</span>
              </button>
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors duration-200">
                <Upload className="w-4 h-4" />
                <span>Importer contacts</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {/* General Settings */}
          {activeTab === 'general' && (
            <div className="p-6 space-y-8">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Informations générales</h2>
                <button
                  onClick={() => toggleEdit('general')}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  <Edit className="w-4 h-4" />
                  <span>{isEditing.general ? 'Annuler' : 'Modifier'}</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom de l'entreprise</label>
                  {isEditing.general ? (
                    <input
                      type="text"
                      value={settings.general.companyName}
                      onChange={(e) => handleInputChange('general', 'companyName', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-gray-50 rounded-xl text-gray-900">{settings.general.companyName}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email principal</label>
                  {isEditing.general ? (
                    <input
                      type="email"
                      value={settings.general.email}
                      onChange={(e) => handleInputChange('general', 'email', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-gray-50 rounded-xl text-gray-900">{settings.general.email}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                  {isEditing.general ? (
                    <input
                      type="tel"
                      value={settings.general.phone}
                      onChange={(e) => handleInputChange('general', 'phone', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-gray-50 rounded-xl text-gray-900">{settings.general.phone}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Site web</label>
                  {isEditing.general ? (
                    <input
                      type="url"
                      value={settings.general.website}
                      onChange={(e) => handleInputChange('general', 'website', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-gray-50 rounded-xl text-gray-900">{settings.general.website}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fuseau horaire</label>
                  <select
                    value={settings.general.timezone}
                    onChange={(e) => handleInputChange('general', 'timezone', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="Europe/Paris">Europe/Paris (UTC+1)</option>
                    <option value="Europe/London">Europe/London (UTC+0)</option>
                    <option value="America/New_York">America/New_York (UTC-5)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Langue</label>
                  <select
                    value={settings.general.language}
                    onChange={(e) => handleInputChange('general', 'language', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="fr">Français</option>
                    <option value="en">English</option>
                    <option value="es">Español</option>
                  </select>
                </div>
              </div>

              {isEditing.general && (
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => toggleEdit('general')}
                    className="px-6 py-3 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => toggleEdit('general')}
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200"
                  >
                    Sauvegarder
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Notifications Settings */}
          {activeTab === 'notifications' && (
            <div className="p-6 space-y-8">
              <h2 className="text-xl font-semibold text-gray-900">Préférences de notification</h2>

              <div className="space-y-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Méthodes de notification</h3>
                  <div className="space-y-4">
                    <ToggleSwitch
                      enabled={settings.notifications.emailNotifications}
                      onChange={() => handleToggle('notifications', 'emailNotifications')}
                      label="Notifications par email"
                      description="Recevez des notifications importantes par email"
                    />
                    <ToggleSwitch
                      enabled={settings.notifications.smsNotifications}
                      onChange={() => handleToggle('notifications', 'smsNotifications')}
                      label="Notifications SMS"
                      description="Notifications urgentes par SMS"
                    />
                    <ToggleSwitch
                      enabled={settings.notifications.pushNotifications}
                      onChange={() => handleToggle('notifications', 'pushNotifications')}
                      label="Notifications push"
                      description="Notifications dans votre navigateur"
                    />
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Types de notifications</h3>
                  <div className="space-y-4">
                    <ToggleSwitch
                      enabled={settings.notifications.newApplications}
                      onChange={() => handleToggle('notifications', 'newApplications')}
                      label="Nouvelles candidatures"
                      description="Être notifié des nouvelles candidatures reçues"
                    />
                    <ToggleSwitch
                      enabled={settings.notifications.projectUpdates}
                      onChange={() => handleToggle('notifications', 'projectUpdates')}
                      label="Mises à jour de projets"
                      description="Notifications sur l'avancement des projets"
                    />
                    <ToggleSwitch
                      enabled={settings.notifications.messageReceived}
                      onChange={() => handleToggle('notifications', 'messageReceived')}
                      label="Messages reçus"
                      description="Notifications de nouveaux messages"
                    />
                    <ToggleSwitch
                      enabled={settings.notifications.weeklyReports}
                      onChange={() => handleToggle('notifications', 'weeklyReports')}
                      label="Rapports hebdomadaires"
                      description="Résumé de votre activité chaque semaine"
                    />
                    <ToggleSwitch
                      enabled={settings.notifications.securityAlerts}
                      onChange={() => handleToggle('notifications', 'securityAlerts')}
                      label="Alertes de sécurité"
                      description="Notifications critiques de sécurité"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Privacy Settings */}
          {activeTab === 'privacy' && (
            <div className="p-6 space-y-8">
              <h2 className="text-xl font-semibold text-gray-900">Confidentialité et sécurité</h2>

              <div className="space-y-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Visibilité du profil</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Visibilité</label>
                      <select
                        value={settings.privacy.profileVisibility}
                        onChange={(e) => handleInputChange('privacy', 'profileVisibility', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="public">Public - Visible par tous</option>
                        <option value="freelancesOnly">Freelances uniquement</option>
                        <option value="private">Privé - Invisible</option>
                      </select>
                    </div>
                    <ToggleSwitch
                      enabled={settings.privacy.searchEngineIndexing}
                      onChange={() => handleToggle('privacy', 'searchEngineIndexing')}
                      label="Indexation moteurs de recherche"
                      description="Permettre aux moteurs de recherche d'indexer votre profil"
                    />
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Données et vie privée</h3>
                  <div className="space-y-4">
                    <ToggleSwitch
                      enabled={settings.privacy.dataSharing}
                      onChange={() => handleToggle('privacy', 'dataSharing')}
                      label="Partage de données"
                      description="Partager des données anonymisées pour améliorer le service"
                    />
                    <ToggleSwitch
                      enabled={settings.privacy.analytics}
                      onChange={() => handleToggle('privacy', 'analytics')}
                      label="Analytics et statistiques"
                      description="Collecter des données d'utilisation pour les statistiques"
                    />
                    <ToggleSwitch
                      enabled={settings.privacy.thirdPartyIntegrations}
                      onChange={() => handleToggle('privacy', 'thirdPartyIntegrations')}
                      label="Intégrations tierces"
                      description="Autoriser les intégrations avec des services tiers"
                    />
                  </div>
                </div>

                <div className="bg-red-50 rounded-xl p-6 border border-red-100">
                  <h3 className="text-lg font-medium text-red-700 mb-4">Zone de danger</h3>
                  <div className="space-y-4">
                    <button className="flex items-center space-x-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 transition-colors duration-200">
                      <Download className="w-4 h-4" />
                      <span>Télécharger mes données</span>
                    </button>
                    <button className="flex items-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200">
                      <Trash2 className="w-4 h-4" />
                      <span>Supprimer mon compte</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Billing Settings */}
          {activeTab === 'billing' && (
            <div className="p-6 space-y-8">
              <h2 className="text-xl font-semibold text-gray-900">Facturation et abonnement</h2>

              <div className="space-y-6">
                {/* Current Plan */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Plan actuel: {settings.billing.plan}</h3>
                      <p className="text-sm text-gray-600">Prochaine facturation: {settings.billing.nextBilling}</p>
                    </div>
                    <button className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200">
                      Changer de plan
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">∞</div>
                      <div className="text-sm text-gray-600">Projets illimités</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">10</div>
                      <div className="text-sm text-gray-600">Utilisateurs max</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">24/7</div>
                      <div className="text-sm text-gray-600">Support prioritaire</div>
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Méthode de paiement</h3>
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white">
                    <div className="flex items-center space-x-3">
                      <CreditCard className="w-6 h-6 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">{settings.billing.paymentMethod}</p>
                        <p className="text-sm text-gray-500">Expire 12/25</p>
                      </div>
                    </div>
                    <button className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                      Modifier
                    </button>
                  </div>
                </div>

                {/* Billing Address */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Adresse de facturation</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
                      <input
                        type="text"
                        value={settings.billing.billingAddress.street}
                        onChange={(e) => handleInputChange('billing', 'billingAddress', {...settings.billing.billingAddress, street: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ville</label>
                      <input
                        type="text"
                        value={settings.billing.billingAddress.city}
                        onChange={(e) => handleInputChange('billing', 'billingAddress', {...settings.billing.billingAddress, city: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Auto-renew */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <ToggleSwitch
                    enabled={settings.billing.autoRenew}
                    onChange={() => handleToggle('billing', 'autoRenew')}
                    label="Renouvellement automatique"
                    description="Renouveler automatiquement votre abonnement"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Team Settings */}
          {activeTab === 'team' && (
            <div className="p-6 space-y-8">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Gestion d'équipe</h2>
                <button className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200">
                  <Plus className="w-4 h-4" />
                  <span>Inviter un membre</span>
                </button>
              </div>

              <div className="space-y-6">
                {/* Team Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                    <div className="text-2xl font-bold text-blue-600 mb-2">{settings.team.currentUsers}/{settings.team.maxUsers}</div>
                    <div className="text-sm text-gray-600">Utilisateurs actifs</div>
                  </div>
                  <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                    <div className="text-2xl font-bold text-green-600 mb-2">2</div>
                    <div className="text-sm text-gray-600">Invitations en attente</div>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-6 border border-purple-100">
                    <div className="text-2xl font-bold text-purple-600 mb-2">5</div>
                    <div className="text-sm text-gray-600">Rôles personnalisés</div>
                  </div>
                </div>

                {/* Team Settings */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Paramètres d'équipe</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Rôle par défaut</label>
                      <select
                        value={settings.team.defaultRole}
                        onChange={(e) => handleInputChange('team', 'defaultRole', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="viewer">Observateur</option>
                        <option value="member">Membre</option>
                        <option value="admin">Administrateur</option>
                      </select>
                    </div>
                    <ToggleSwitch
                      enabled={settings.team.requireApproval}
                      onChange={() => handleToggle('team', 'requireApproval')}
                      label="Approbation requise"
                      description="Les nouvelles invitations nécessitent une approbation admin"
                    />
                    <ToggleSwitch
                      enabled={settings.team.ssoEnabled}
                      onChange={() => handleToggle('team', 'ssoEnabled')}
                      label="Single Sign-On (SSO)"
                      description="Authentification via SSO d'entreprise"
                    />
                  </div>
                </div>

                {/* Team Members */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Membres de l'équipe</h3>
                  <div className="space-y-3">
                    {[
                      { name: "Antoine Da Silva", email: "antoine@greentech-solutions.com", role: "Admin", status: "active" },
                      { name: "Marie Dubois", email: "marie@greentech-solutions.com", role: "Membre", status: "active" },
                      { name: "Pierre Martin", email: "pierre@greentech-solutions.com", role: "Observateur", status: "pending" }
                    ].map((member, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center text-white font-bold">
                            {member.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{member.name}</p>
                            <p className="text-sm text-gray-500">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            member.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {member.role}
                          </span>
                          <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                            <Edit className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* API Settings */}
          {activeTab === 'api' && (
            <div className="p-6 space-y-8">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">API et Intégrations</h2>
                <button className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200">
                  <Plus className="w-4 h-4" />
                  <span>Nouvelle clé API</span>
                </button>
              </div>

              <div className="space-y-6">
                {/* API Status */}
                <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                  <div className="flex items-center space-x-3 mb-4">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <h3 className="text-lg font-medium text-gray-900">API activée</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <div className="text-2xl font-bold text-green-600">{settings.api.rateLimit}</div>
                      <div className="text-sm text-gray-600">Requêtes/heure</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">99.9%</div>
                      <div className="text-sm text-gray-600">Disponibilité</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">156ms</div>
                      <div className="text-sm text-gray-600">Latence moyenne</div>
                    </div>
                  </div>
                </div>

                {/* API Keys */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Clés API</h3>
                  <div className="space-y-3">
                    {settings.api.apiKeys.map((key) => (
                      <div key={key.id} className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                        <div>
                          <p className="font-medium text-gray-900">{key.name}</p>
                          <p className="text-sm text-gray-500 font-mono">{key.key}</p>
                          <p className="text-xs text-gray-400">Créée le {key.created} • Dernière utilisation: {key.lastUsed}</p>
                        </div>
                        <div className="flex space-x-2">
                          <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                            <Eye className="w-4 h-4 text-gray-600" />
                          </button>
                          <button className="p-2 hover:bg-red-100 rounded-lg transition-colors duration-200">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Webhooks */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Webhooks</h3>
                  <ToggleSwitch
                    enabled={settings.api.webhooksEnabled}
                    onChange={() => handleToggle('api', 'webhooksEnabled')}
                    label="Webhooks activés"
                    description="Recevoir des notifications en temps réel via webhooks"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsComponent;